const DEFAULT_LIMITS = Object.freeze({
    green: 60,
    yellow: 85,
    orange: null,
    red: 99,
})

export const ZONE = Object.freeze({
    GREEN: 'green',
    YELLOW: 'yellow',
    ORANGE: 'orange',
    RED: 'red',
})

const ZONE_TO_VUETIFY = Object.freeze({
    [ZONE.GREEN]: 'success',
    [ZONE.YELLOW]: 'warning',
    [ZONE.ORANGE]: 'orange',
    [ZONE.RED]: 'error',
})

/**
 * Retorna a "zona" (verde/amarelo/laranja/vermelho) com base nos limites
 * dinâmicos vindos de `occupation_settings`. Se algum limite vier nulo,
 * cai para o default das Fundações.
 */
export function resolveOccupationZone(percentage, limits) {
    const cfg = { ...DEFAULT_LIMITS, ...(limits || {}) }
    if (percentage >= cfg.red) return ZONE.RED
    if (cfg.orange != null && percentage >= cfg.orange) return ZONE.ORANGE
    if (percentage >= cfg.yellow) return ZONE.YELLOW
    return ZONE.GREEN
}

export function zoneToColor(zone) {
    return ZONE_TO_VUETIFY[zone] || 'grey'
}

export const CAPACITY_MODE = Object.freeze({
    REDUCED: 'RED',
    CONVENTIONAL: 'CONV',
    EXTENDED: 'AMP',
})

const CAPACITY_MODE_LABEL = Object.freeze({
    [CAPACITY_MODE.REDUCED]: 'Reduzida',
    [CAPACITY_MODE.CONVENTIONAL]: 'Convencional',
    [CAPACITY_MODE.EXTENDED]: 'Ampliada',
})

/**
 * Retorna a capacidade que deve ser usada como denominador no cálculo de ocupação.
 *
 * Why: o ambiente tem três valores (reduced/capacity/extended) e um modo ativo
 * (`capacity_mode`) que decide qual vale. O backend já expõe `active_capacity`
 * via accessor; esta função serve como fallback robusto quando a resposta
 * simplificada traz só `capacity` ou quando o modo não foi definido.
 */
export function getActiveCapacity(ambience) {
    if (!ambience) return 0
    if (ambience.active_capacity != null) return ambience.active_capacity

    switch (ambience.capacity_mode) {
        case CAPACITY_MODE.REDUCED:
            return ambience.reduced_capacity ?? ambience.capacity ?? 0
        case CAPACITY_MODE.EXTENDED:
            return ambience.extended_capacity ?? ambience.capacity ?? 0
        default:
            return ambience.capacity ?? 0
    }
}

export function getCapacityModeLabel(capacityMode) {
    return CAPACITY_MODE_LABEL[capacityMode] || null
}

export function getOccupationPercentage(ambience, occupation) {
    const capacity = getActiveCapacity(ambience)
    if (!capacity || !occupation?.students) return 0
    return Math.round((occupation.students / capacity) * 100)
}

/**
 * Cor do card alinhada às Fundações (`occupation_settings`).
 *
 * @param {object} ambience
 * @param {object} occupation
 * @param {object} [limits] - { green, yellow, orange, red } vindos da API.
 *                            Se ausente, usa defaults compatíveis com OccupationSetting::DEFAULT_*.
 */
export function getOccupationColor(ambience, occupation, limits) {
    if (!occupation) return 'grey'
    const percentage = getOccupationPercentage(ambience, occupation)
    return zoneToColor(resolveOccupationZone(percentage, limits))
}

export function getBalanceColor(balance) {
    if (balance == null) return 'grey'
    if (balance > 0) return 'success'
    if (balance === 0) return 'info'
    return 'error'
}
